<h2 mat-dialog-title>Walk-in Dispense</h2>
<mat-dialog-content class="walkin-content">
    <div class="walkin-shell">
        <section class="section-card patient-card">
            <header>
                <p class="eyebrow">Patient Snapshot</p>
                <h3>Create a quick patient shell before dispensing</h3>
                <p class="muted">Name is mandatory. Birth date and gender help match returning visitors.</p>
            </header>
            <form class="patient-grid walk-in-dispense" [formGroup]="patientForm">
                <mat-form-field appearance="outline" [floatLabel]="'always'">
                    <mat-label>Patient name</mat-label>
                    <input matInput placeholder="e.g., Ada Okafor" formControlName="name">
                    <mat-error *ngIf="patientForm.get('name')?.hasError('required')">Name is required.</mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" [floatLabel]="'always'">
                    <mat-label>Birth date</mat-label>
                    <input matInput [matDatepicker]="birthdayPicker" formControlName="birthDate"
                        placeholder="YYYY-MM-DD">
                    <mat-datepicker-toggle matIconSuffix [for]="birthdayPicker"></mat-datepicker-toggle>
                    <mat-datepicker #birthdayPicker></mat-datepicker>
                </mat-form-field>
                <mat-form-field appearance="outline">
                    <mat-label>Gender</mat-label>
                    <mat-select formControlName="gender">
                        <mat-option *ngFor="let option of genderOptions" [value]="option.value">{{ option.label
                            }}</mat-option>
                    </mat-select>
                </mat-form-field>
            </form>
        </section>

        <section class="section-card inventory-card">
            <header>
                <p class="eyebrow">Inventory Lookup</p>
                <h3>Search active stock and queue medications</h3>
            </header>
            <mat-form-field appearance="fill" class="search-field">
                <mat-label>Search inventory</mat-label>
                <input matInput placeholder="Type an active ingredient or brand" [formControl]="inventorySearchCtrl"
                    [matAutocomplete]="inventoryAuto">
                @if (inventorySearchCtrl.value) {
                <button mat-icon-button matSuffix type="button" (click)="clearSearch()" matTooltip="Clear search">
                    <mat-icon>close</mat-icon>
                </button>
                } @else {
                <mat-icon matSuffix>search</mat-icon>
                }
            </mat-form-field>
            <mat-autocomplete #inventoryAuto="matAutocomplete" [displayWith]="displayMedication"
                (optionSelected)="handleMedicationSelected($event.option.value)">
                @let matches = (inventoryResults$ | async) ?? [];
                @if (matches.length === 0) {
                <mat-option disabled>No inventory matches yet. Keep typing to narrow down.</mat-option>
                } @else {
                @for (med of matches; track med.id || med.code?.text || $index) {
                <mat-option class="inventory-option my-8" [value]="med">
                    @let stock = getMedicationStockLabel(med);
                    <article class="search-result-card option-card editor-header">
                        <div class="editor-info">
                            <p class="title">{{ getMedicationTitle(med) }}</p>
                            <p class="subtitle">{{ getMedicationSubtitle(med) || 'No form details' }}</p>
                        </div>
                        <!-- <div class="option-meta">
                            <p class="meta-label">Status</p>
                            <p class="meta-value">{{ med.status || 'Not specified' }}</p>
                        </div> -->
                        <div class="option-stock">
                            <span class="stock-badge" [class.missing]="!stock">
                                {{ stock || 'Stock unknown' }}
                            </span>
                        </div>
                    </article>
                </mat-option>
                }
                }
            </mat-autocomplete>
            <button mat-flat-button color="primary" type="button" (click)="addSelectedMedication()"
                [disabled]="!selectedMedication" style="margin-top: 12px;">
                Add medication
            </button>
        </section>

        <section class="section-card selection-card" [formGroup]="form">
            <header>
                <p class="eyebrow">Selected Medications</p>
                <div class="summary">
                    <h3>{{ medicationFormArray.length }} item(s)</h3>
                    <p>{{ totalQuantity }} total unit(s) queued</p>
                </div>
            </header>
            <div formArrayName="medications">
                @if (hasSelectedMedications) {
                <div class="medication-stack">
                    @for (group of medicationFormArray.controls; track trackMedication($index, group); let i = $index) {
                    <article class="medication-editor" [formGroupName]="i">
                        @let stockLabel = getMedicationStockLabel(group.get('medication')?.value);

                        <div class="editor-header">
                            <div class="editor-info">

                                <p class="title g-just-flex gap-4 justify-content-start">
                                    <span>{{
                                        getMedicationTitle(group.get('medication')?.value) }}</span>

                                </p>
                                <p class="subtitle">
                                    <span>{{ getMedicationSubtitle(group.get('medication')?.value) || 'Stock
                                        item'
                                        }}</span>
                                </p>
                                @if (stockLabel) {
                                <div class="gap m-4"></div>
                                <p class="stock-badge">{{ stockLabel }}</p>
                                }

                            </div>
                            @let quantityCap = getMedicationQuantityCap(group);
                            <div class="quantity-control">
                                <button mat-icon-button type="button" color="primary" aria-label="Decrease quantity"
                                    (click)="adjustQuantity(i, -1)" [disabled]="getQuantityValue(group) <= 1">
                                    <mat-icon>remove</mat-icon>
                                </button>
                                <mat-form-field appearance="outline" class="quantity-field">
                                    <!-- <mat-label>Quantity to dispense</mat-label> -->
                                    <input type="number" min="1" [max]="quantityCap ?? null" matInput
                                        style="text-align: center;"
                                        matTooltip="{{ quantityCap ? `Maximum ${quantityCap} unit(s) available in stock` : '' }}"
                                        formControlName="quantity">
                                    <mat-error *ngIf="group.get('quantity')?.hasError('min')">At least one unit is
                                        required.</mat-error>
                                </mat-form-field>
                                <button mat-icon-button type="button" color="primary" aria-label="Increase quantity"
                                    (click)="adjustQuantity(i, 1)"
                                    [disabled]="quantityCap !== null && getQuantityValue(group) >= quantityCap">
                                    <mat-icon>add</mat-icon>
                                </button>
                            </div>
                            <div class="dosage-slot">
                                <p class="eyebrow">Dosage</p>
                                <p class="dosage-value" [class.missing]="!hasDosage(group)">
                                    {{ getDosageSummary(group) }}
                                </p>
                                <button mat-stroked-button color="primary" type="button"
                                    (click)="openDosageDialog(group)">
                                    <mat-icon>{{ hasDosage(group) ? 'visibility' : 'note_add' }}</mat-icon>
                                    {{ hasDosage(group) ? 'View dosage' : 'Add dosage' }}
                                </button>
                                @if (group.get('dosageInstruction')?.invalid && (group.get('dosageInstruction')?.dirty
                                || group.get('dosageInstruction')?.touched)) {
                                <p class="dosage-error">Dosage text is required.</p>
                                }
                            </div>
                            <button mat-icon-button color="warn" type="button" (click)="removeMedication(i)"
                                class="delete-med-btn floating-delete" matTooltip="Remove">
                                <mat-icon>delete</mat-icon>
                            </button>
                        </div>
                    </article>
                    }
                </div>
                } @else {
                <div class="empty-selection">
                    <mat-icon>local_pharmacy</mat-icon>
                    <p>Add medications from the inventory search to begin dispensing.</p>
                </div>
                }
            </div>
        </section>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button type="button" (click)="cancel()">Cancel</button>
    <button mat-flat-button color="primary" type="button" (click)="submit()"
        [disabled]="submitting || !hasSelectedMedications || form.invalid">
        @if (submitting) {
        <mat-progress-spinner diameter="18" mode="indeterminate"></mat-progress-spinner>
        } @else {
        Submit Dispense
        }
    </button>
</mat-dialog-actions>